import React, { useState, useRef } from 'react';
import { theme, fontStack, bodyFont } from '../theme';
import { useChurchData } from '../data/ChurchDataProvider';

/* ═══════════════════════════════════════
   Admin Portal
   - Password: "npbc2024" (change in code)
   - Tabs: Bulletin, Staff, Ministries
   - Export JSON for deployment
   ═══════════════════════════════════════ */

const ADMIN_PASSWORD = "npbc121314";

// ── Styles ──
const s = {
  input: {
    width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.05)",
    border: `1px solid ${theme.border}`, color: theme.cream,
    fontFamily: bodyFont, fontSize: 14, outline: "none", boxSizing: "border-box",
  },
  textarea: {
    width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.05)",
    border: `1px solid ${theme.border}`, color: theme.cream,
    fontFamily: bodyFont, fontSize: 14, outline: "none", minHeight: 80,
    resize: "vertical", boxSizing: "border-box",
  },
  label: { fontFamily: bodyFont, fontSize: 12, color: theme.gold, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4, display: "block" },
  card: { padding: 20, background: theme.bgCard, border: `1px solid ${theme.border}`, marginBottom: 12 },
  btnPrimary: {
    padding: "10px 24px", background: theme.gold, border: "none", color: theme.bg,
    fontFamily: bodyFont, fontSize: 12, fontWeight: 600, cursor: "pointer",
    letterSpacing: "0.1em", textTransform: "uppercase", transition: "all 0.3s",
  },
  btnDanger: {
    padding: "8px 16px", background: "transparent", border: `1px solid #c0392b`,
    color: "#e74c3c", fontFamily: bodyFont, fontSize: 11, fontWeight: 600,
    cursor: "pointer", letterSpacing: "0.1em", textTransform: "uppercase",
  },
  btnOutline: {
    padding: "8px 16px", background: "transparent", border: `1px solid ${theme.border}`,
    color: theme.textMuted, fontFamily: bodyFont, fontSize: 11, fontWeight: 600,
    cursor: "pointer", letterSpacing: "0.1em", textTransform: "uppercase",
  },
};

// ── Login Screen ──
function LoginScreen({ onLogin }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState(false);
  return (
    <div style={{ paddingTop: 140, minHeight: "80vh", display: "flex", justifyContent: "center" }}>
      <div style={{ width: 360 }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div style={{ fontFamily: bodyFont, fontSize: 11, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 12 }}>Admin Portal</div>
          <h1 style={{ fontFamily: fontStack, fontSize: 32, fontWeight: 400, color: theme.cream }}>Staff Login</h1>
        </div>
        <div style={s.card}>
          <label style={s.label}>Password</label>
          <input
            type="password" value={pw}
            onChange={e => { setPw(e.target.value); setError(false); }}
            onKeyDown={e => { if (e.key === "Enter") { pw === ADMIN_PASSWORD ? onLogin() : setError(true); } }}
            style={{ ...s.input, marginBottom: 16, borderColor: error ? "#e74c3c" : theme.border }}
            placeholder="Enter admin password"
          />
          {error && <p style={{ fontFamily: bodyFont, fontSize: 12, color: "#e74c3c", marginBottom: 12 }}>Incorrect password. Please try again.</p>}
          <button style={s.btnPrimary} onClick={() => pw === ADMIN_PASSWORD ? onLogin() : setError(true)}>
            Sign In
          </button>
        </div>
        <p style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted, textAlign: "center", marginTop: 16 }}>
          Contact the church office if you need access.
        </p>
      </div>
    </div>
  );
}

// ── Church Info Manager ──
function ChurchInfoTab() {
  const { data, setField } = useChurchData();
  const info = data.churchInfo || {};
  const [draft, setDraft] = useState({ ...info });
  const [saved, setSaved] = useState(false);

  const update = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));

  const handleSave = () => {
    setField("churchInfo", { ...draft });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const sections = [
    {
      title: "Church Identity",
      fields: [
        { key: "tagline", label: "Tagline / Motto", type: "text", placeholder: "Shown on homepage and throughout the site" },
        { key: "subtitle", label: "Subtitle", type: "text", placeholder: "Secondary line below the main heading" },
        { key: "mission", label: "Mission Statement", type: "textarea", placeholder: "Our church mission..." },
        { key: "vision", label: "Vision Statement", type: "textarea", placeholder: "Our church vision..." },
        { key: "coreValues", label: "Core Values (comma-separated)", type: "text", placeholder: "Missions, Worship, Ministry, Fellowship, Discipleship, Prayer" },
      ],
    },
    {
      title: "Monthly Scripture",
      fields: [
        { key: "monthlyVerseRef", label: "Scripture Reference", type: "text", placeholder: "e.g. Matthew 11:28-30" },
        { key: "monthlyVerseText", label: "Scripture Text", type: "textarea", placeholder: "The verse text..." },
      ],
    },
    {
      title: "Contact Information",
      fields: [
        { key: "phone", label: "Phone Number", type: "text", placeholder: "318-640-9712" },
        { key: "email", label: "Email", type: "text", placeholder: "info@npbcdryprong.com" },
        { key: "website", label: "Website", type: "text", placeholder: "www.npbcdryprong.com" },
        { key: "eGiving", label: "E-Giving Instructions", type: "text", placeholder: "Text NPBC to 73256" },
      ],
    },
    {
      title: "Address",
      fields: [
        { key: "street", label: "Street", type: "text", placeholder: "111 Prospect Church Road" },
        { key: "city", label: "City", type: "text", placeholder: "Dry Prong" },
        { key: "state", label: "State", type: "text", placeholder: "LA" },
        { key: "zip", label: "ZIP Code", type: "text", placeholder: "71423" },
      ],
    },
    {
      title: "Social Media & YouTube",
      fields: [
        { key: "facebookPageUrl", label: "Facebook Page URL", type: "text", placeholder: "https://www.facebook.com/..." },
        { key: "youtubeHandle", label: "YouTube Handle (without @)", type: "text", placeholder: "newprospectbaptistchurchin6689" },
        { key: "youtubeChannelId", label: "YouTube Channel ID (optional, speeds up loading)", type: "text", placeholder: "UC..." },
      ],
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream }}>Church Information</h3>
        <button
          style={{ ...s.btnPrimary, background: saved ? "#27ae60" : theme.gold }}
          onClick={handleSave}
        >
          {saved ? "✓ Applied!" : "Apply Changes"}
        </button>
      </div>
      <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginBottom: 24 }}>
        Edit your church's core information. Click "Apply Changes" to update the site, then "Save Changes" at the top to publish.
      </p>

      {sections.map((section, si) => (
        <div key={si} style={{ ...s.card, marginBottom: 16 }}>
          <h4 style={{ fontFamily: fontStack, fontSize: 18, fontWeight: 500, color: theme.gold, marginBottom: 16 }}>{section.title}</h4>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {section.fields.map(f => (
              <div key={f.key}>
                <label style={s.label}>{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea style={s.textarea} value={draft[f.key] || ""} onChange={e => update(f.key, e.target.value)} placeholder={f.placeholder} />
                ) : (
                  <input style={s.input} value={draft[f.key] || ""} onChange={e => update(f.key, e.target.value)} placeholder={f.placeholder} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Bulletin Manager ──
function BulletinTab() {
  const { data, updateItem, addItem, removeItem } = useChurchData();
  const [editing, setEditing] = useState(null);

  const emptyItem = { date: "", category: "", title: "", desc: "", featured: false };
  const [newItem, setNewItem] = useState(emptyItem);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream }}>
          Bulletin & Announcements ({data.newsItems.length})
        </h3>
      </div>

      {/* Existing items */}
      {data.newsItems.map((item, i) => (
        <div key={item.id} style={s.card}>
          {editing === item.id ? (
            <EditForm item={item} fields={bulletinFields}
              onSave={(updates) => { updateItem("newsItems", item.id, updates); setEditing(null); }}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
                  <span style={{ fontFamily: bodyFont, fontSize: 11, color: theme.gold, fontWeight: 600 }}>{item.category}</span>
                  <span style={{ fontFamily: bodyFont, fontSize: 11, color: theme.textMuted }}>• {item.date}</span>
                  {item.featured && <span style={{ fontFamily: bodyFont, fontSize: 9, color: theme.bg, background: theme.gold, padding: "2px 6px", fontWeight: 700 }}>FEATURED</span>}
                </div>
                <h4 style={{ fontFamily: fontStack, fontSize: 17, fontWeight: 500, color: theme.cream, marginBottom: 4 }}>{item.title}</h4>
                <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, lineHeight: 1.5 }}>{item.desc}</p>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button style={s.btnOutline} onClick={() => setEditing(item.id)}>Edit</button>
                <button style={s.btnDanger} onClick={() => { if (window.confirm(`Remove "${item.title}"?`)) removeItem("newsItems", item.id); }}>Remove</button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add new */}
      <div style={{ ...s.card, borderColor: theme.borderHover, marginTop: 24 }}>
        <h4 style={{ fontFamily: fontStack, fontSize: 16, fontWeight: 500, color: theme.gold, marginBottom: 16 }}>+ Add Announcement</h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={s.label}>Date</label><input style={s.input} value={newItem.date} onChange={e => setNewItem({ ...newItem, date: e.target.value })} placeholder="e.g. Mar 15" /></div>
          <div><label style={s.label}>Category</label><input style={s.input} value={newItem.category} onChange={e => setNewItem({ ...newItem, category: e.target.value })} placeholder="e.g. Fundraiser" /></div>
        </div>
        <div style={{ marginTop: 12 }}><label style={s.label}>Title</label><input style={s.input} value={newItem.title} onChange={e => setNewItem({ ...newItem, title: e.target.value })} placeholder="Announcement title" /></div>
        <div style={{ marginTop: 12 }}><label style={s.label}>Description</label><textarea style={s.textarea} value={newItem.desc} onChange={e => setNewItem({ ...newItem, desc: e.target.value })} placeholder="Details about this announcement..." /></div>
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={newItem.featured} onChange={e => setNewItem({ ...newItem, featured: e.target.checked })} id="featured-check" />
          <label htmlFor="featured-check" style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted }}>Mark as featured (shows "This Week" badge)</label>
        </div>
        <button style={{ ...s.btnPrimary, marginTop: 16 }} onClick={() => {
          if (!newItem.title.trim()) return alert("Title is required");
          addItem("newsItems", { ...newItem });
          setNewItem(emptyItem);
        }}>Add Announcement</button>
      </div>
    </div>
  );
}

// ── Staff Manager ──
function StaffTab() {
  const { data, updateItem, addItem, removeItem } = useChurchData();
  const [editing, setEditing] = useState(null);
  const emptyStaff = { name: "", role: "", desc: "", photo: null };
  const [newItem, setNewItem] = useState(emptyStaff);

  return (
    <div>
      <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 24 }}>
        Church Staff ({data.leaders.length})
      </h3>

      {data.leaders.map((person) => (
        <div key={person.id} style={s.card}>
          {editing === person.id ? (
            <StaffEditForm person={person}
              onSave={(updates) => { updateItem("leaders", person.id, updates); setEditing(null); }}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{
                width: 60, height: 60, borderRadius: "50%", flexShrink: 0,
                background: person.photo ? `url(${person.photo}) center/cover` : `linear-gradient(135deg, ${theme.accent}, ${theme.bgCard})`,
                border: `2px solid ${theme.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                overflow: "hidden",
              }}>
                {!person.photo && <span style={{ fontFamily: fontStack, fontSize: 22, color: theme.goldDark }}>{person.name.charAt(0)}</span>}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: bodyFont, fontSize: 10, color: theme.gold, letterSpacing: "0.15em", textTransform: "uppercase" }}>{person.role}</div>
                <h4 style={{ fontFamily: fontStack, fontSize: 18, fontWeight: 500, color: theme.cream }}>{person.name}</h4>
                <p style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted }}>{person.desc}</p>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button style={s.btnOutline} onClick={() => setEditing(person.id)}>Edit</button>
                <button style={s.btnDanger} onClick={() => { if (window.confirm(`Remove ${person.name}?`)) removeItem("leaders", person.id); }}>Remove</button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add new staff */}
      <div style={{ ...s.card, borderColor: theme.borderHover, marginTop: 24 }}>
        <h4 style={{ fontFamily: fontStack, fontSize: 16, fontWeight: 500, color: theme.gold, marginBottom: 16 }}>+ Add Staff Member</h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={s.label}>Name</label><input style={s.input} value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} placeholder="Full name" /></div>
          <div><label style={s.label}>Role / Title</label><input style={s.input} value={newItem.role} onChange={e => setNewItem({ ...newItem, role: e.target.value })} placeholder="e.g. Deacon" /></div>
        </div>
        <div style={{ marginTop: 12 }}><label style={s.label}>Description</label><textarea style={s.textarea} value={newItem.desc} onChange={e => setNewItem({ ...newItem, desc: e.target.value })} placeholder="Brief description of their role..." /></div>
        <div style={{ marginTop: 12 }}><label style={s.label}>Photo</label><PhotoUpload onUpload={(base64) => setNewItem({ ...newItem, photo: base64 })} currentPhoto={newItem.photo} /></div>
        <button style={{ ...s.btnPrimary, marginTop: 16 }} onClick={() => {
          if (!newItem.name.trim()) return alert("Name is required");
          addItem("leaders", { ...newItem });
          setNewItem(emptyStaff);
        }}>Add Staff Member</button>
      </div>
    </div>
  );
}

// ── Ministries Manager ──
function MinistriesTab() {
  const { data, updateItem, addItem, removeItem } = useChurchData();
  const [editing, setEditing] = useState(null);
  const emptyMinistry = { name: "", desc: "", icon: "📋" };
  const [newItem, setNewItem] = useState(emptyMinistry);

  const iconOptions = ["🙏", "👧", "👕", "🧵", "❤️", "🌍", "🎵", "💪", "📖", "🏕️", "🍲", "✝️", "🏠", "🎶", "📋"];

  return (
    <div>
      <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 24 }}>
        Ministries & Programs ({data.ministries.length})
      </h3>

      {data.ministries.map((ministry) => (
        <div key={ministry.id} style={s.card}>
          {editing === ministry.id ? (
            <MinistryEditForm ministry={ministry} iconOptions={iconOptions}
              onSave={(updates) => { updateItem("ministries", ministry.id, updates); setEditing(null); }}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
              <span style={{ fontSize: 28, flexShrink: 0 }}>{ministry.icon}</span>
              <div style={{ flex: 1 }}>
                <h4 style={{ fontFamily: fontStack, fontSize: 18, fontWeight: 500, color: theme.cream, marginBottom: 4 }}>{ministry.name}</h4>
                <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, lineHeight: 1.5 }}>{ministry.desc}</p>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button style={s.btnOutline} onClick={() => setEditing(ministry.id)}>Edit</button>
                <button style={s.btnDanger} onClick={() => { if (window.confirm(`Remove "${ministry.name}"?`)) removeItem("ministries", ministry.id); }}>Remove</button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add new */}
      <div style={{ ...s.card, borderColor: theme.borderHover, marginTop: 24 }}>
        <h4 style={{ fontFamily: fontStack, fontSize: 16, fontWeight: 500, color: theme.gold, marginBottom: 16 }}>+ Add Ministry</h4>
        <div><label style={s.label}>Ministry Name</label><input style={s.input} value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} placeholder="e.g. Vacation Bible School" /></div>
        <div style={{ marginTop: 12 }}><label style={s.label}>Description</label><textarea style={s.textarea} value={newItem.desc} onChange={e => setNewItem({ ...newItem, desc: e.target.value })} placeholder="What this ministry does, when it meets, etc." /></div>
        <div style={{ marginTop: 12 }}>
          <label style={s.label}>Icon</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {iconOptions.map(ico => (
              <button key={ico} onClick={() => setNewItem({ ...newItem, icon: ico })}
                style={{
                  width: 40, height: 40, fontSize: 20, cursor: "pointer",
                  background: newItem.icon === ico ? "rgba(200,164,86,0.2)" : "rgba(255,255,255,0.03)",
                  border: `2px solid ${newItem.icon === ico ? theme.gold : theme.border}`,
                  borderRadius: 4, transition: "all 0.2s",
                }}>{ico}</button>
            ))}
          </div>
        </div>
        <button style={{ ...s.btnPrimary, marginTop: 16 }} onClick={() => {
          if (!newItem.name.trim()) return alert("Name is required");
          addItem("ministries", { ...newItem });
          setNewItem(emptyMinistry);
        }}>Add Ministry</button>
      </div>
    </div>
  );
}

// ── Contact Form Manager ──
function FormTab() {
  const { data, setField } = useChurchData();
  const form = data.formConfig || {};
  const fields = form.fields || [];
  const [draft, setDraft] = useState({
    heading: form.heading || "", subheading: form.subheading || "",
    successMessage: form.successMessage || "", submitLabel: form.submitLabel || "",
    emailService: form.emailService || "none",
    formspreeId: form.formspreeId || "",
    web3formsKey: form.web3formsKey || "",
    emailjsServiceId: form.emailjsServiceId || "",
    emailjsTemplateId: form.emailjsTemplateId || "",
    emailjsPublicKey: form.emailjsPublicKey || "",
    mailtoAddress: form.mailtoAddress || "",
  });
  const [editingField, setEditingField] = useState(null);
  const [saved, setSaved] = useState(false);

  const fieldTypes = [
    { value: "text", label: "Text" },
    { value: "email", label: "Email" },
    { value: "tel", label: "Phone" },
    { value: "number", label: "Number" },
    { value: "select", label: "Dropdown" },
    { value: "textarea", label: "Text Area" },
  ];

  const saveSettings = () => {
    setField("formConfig", { ...form, ...draft, fields });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const updateField = (id, updates) => {
    const updated = fields.map(f => f.id === id ? { ...f, ...updates } : f);
    setField("formConfig", { ...form, ...draft, fields: updated });
  };

  const addField = () => {
    const newField = { id: `f-${Date.now()}`, label: "New Field", type: "text", placeholder: "", required: false, halfWidth: false, enabled: true, options: "" };
    setField("formConfig", { ...form, ...draft, fields: [...fields, newField] });
    setEditingField(newField.id);
  };

  const removeField = (id) => {
    if (!window.confirm("Remove this field?")) return;
    setField("formConfig", { ...form, ...draft, fields: fields.filter(f => f.id !== id) });
  };

  const moveField = (idx, dir) => {
    const arr = [...fields];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    setField("formConfig", { ...form, ...draft, fields: arr });
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream }}>Contact Form Settings</h3>
        <button style={{ ...s.btnPrimary, background: saved ? "#27ae60" : theme.gold }} onClick={saveSettings}>
          {saved ? "✓ Applied!" : "Apply Changes"}
        </button>
      </div>

      {/* General settings */}
      <div style={s.card}>
        <h4 style={{ fontFamily: fontStack, fontSize: 18, fontWeight: 500, color: theme.gold, marginBottom: 16 }}>Page Settings</h4>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div><label style={s.label}>Form Heading</label><input style={s.input} value={draft.heading} onChange={e => setDraft({ ...draft, heading: e.target.value })} placeholder="Get In Touch" /></div>
          <div><label style={s.label}>Subheading</label><input style={s.input} value={draft.subheading} onChange={e => setDraft({ ...draft, subheading: e.target.value })} placeholder="Fill out the form below..." /></div>
          <div><label style={s.label}>Success Message (after submission)</label><textarea style={s.textarea} value={draft.successMessage} onChange={e => setDraft({ ...draft, successMessage: e.target.value })} placeholder="We've received your message..." /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label style={s.label}>Submit Button Text</label><input style={s.input} value={draft.submitLabel} onChange={e => setDraft({ ...draft, submitLabel: e.target.value })} placeholder="Send Message" /></div>
          </div>
        </div>
      </div>

      {/* Email Service Config */}
      <div style={{ ...s.card, marginBottom: 16, borderColor: draft.emailService !== "none" ? "rgba(39,174,96,0.3)" : "rgba(231,76,60,0.2)" }}>
        <h4 style={{ fontFamily: fontStack, fontSize: 18, fontWeight: 500, color: theme.gold, marginBottom: 8 }}>Email Delivery</h4>
        <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginBottom: 16 }}>
          {draft.emailService === "none"
            ? "⚠️ No email service configured. Form submissions won't be delivered. Choose a service below."
            : "✅ Email delivery is configured. Form submissions will be sent to your inbox."}
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={s.label}>Email Service</label>
          <select style={s.input} value={draft.emailService} onChange={e => setDraft({ ...draft, emailService: e.target.value })}>
            <option value="none">Not configured</option>
            <option value="formspree">Formspree (recommended — easiest setup)</option>
            <option value="web3forms">Web3Forms</option>
            <option value="emailjs">EmailJS</option>
            <option value="mailto">Mailto (opens visitor's email app)</option>
          </select>
        </div>

        {draft.emailService === "formspree" && (
          <div style={{ padding: 16, background: "rgba(255,255,255,0.02)", border: `1px solid ${theme.border}`, marginTop: 12 }}>
            <h5 style={{ fontFamily: fontStack, fontSize: 14, color: theme.cream, marginBottom: 8 }}>Formspree Setup</h5>
            <p style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted, lineHeight: 1.6, marginBottom: 12 }}>
              1. Go to <strong style={{ color: theme.cream }}>formspree.io</strong> → Sign up free<br />
              2. Create a new form → Choose email to receive submissions<br />
              3. Copy the Form ID from the endpoint (e.g. if endpoint is formspree.io/f/<strong style={{ color: theme.gold }}>xpzvqkdl</strong>, the ID is <strong style={{ color: theme.gold }}>xpzvqkdl</strong>)
            </p>
            <label style={s.label}>Formspree Form ID</label>
            <input style={s.input} value={draft.formspreeId} onChange={e => setDraft({ ...draft, formspreeId: e.target.value })} placeholder="xpzvqkdl" />
          </div>
        )}

        {draft.emailService === "web3forms" && (
          <div style={{ padding: 16, background: "rgba(255,255,255,0.02)", border: `1px solid ${theme.border}`, marginTop: 12 }}>
            <h5 style={{ fontFamily: fontStack, fontSize: 14, color: theme.cream, marginBottom: 8 }}>Web3Forms Setup</h5>
            <p style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted, lineHeight: 1.6, marginBottom: 12 }}>
              1. Go to <strong style={{ color: theme.cream }}>web3forms.com</strong><br />
              2. Enter your email → Get your Access Key<br />
              3. Paste it below
            </p>
            <label style={s.label}>Access Key</label>
            <input style={s.input} value={draft.web3formsKey} onChange={e => setDraft({ ...draft, web3formsKey: e.target.value })} placeholder="your-access-key-here" />
          </div>
        )}

        {draft.emailService === "emailjs" && (
          <div style={{ padding: 16, background: "rgba(255,255,255,0.02)", border: `1px solid ${theme.border}`, marginTop: 12 }}>
            <h5 style={{ fontFamily: fontStack, fontSize: 14, color: theme.cream, marginBottom: 8 }}>EmailJS Setup</h5>
            <p style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted, lineHeight: 1.6, marginBottom: 12 }}>
              1. Go to <strong style={{ color: theme.cream }}>emailjs.com</strong> → Create free account<br />
              2. Add an Email Service (Gmail, Outlook, etc.)<br />
              3. Create an Email Template (use {"{{message}}"} for form content)<br />
              4. Copy the three IDs below from your dashboard
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div><label style={s.label}>Service ID</label><input style={s.input} value={draft.emailjsServiceId} onChange={e => setDraft({ ...draft, emailjsServiceId: e.target.value })} placeholder="service_xxxxxx" /></div>
              <div><label style={s.label}>Template ID</label><input style={s.input} value={draft.emailjsTemplateId} onChange={e => setDraft({ ...draft, emailjsTemplateId: e.target.value })} placeholder="template_xxxxxx" /></div>
              <div><label style={s.label}>Public Key</label><input style={s.input} value={draft.emailjsPublicKey} onChange={e => setDraft({ ...draft, emailjsPublicKey: e.target.value })} placeholder="your_public_key" /></div>
            </div>
          </div>
        )}

        {draft.emailService === "mailto" && (
          <div style={{ padding: 16, background: "rgba(255,255,255,0.02)", border: `1px solid ${theme.border}`, marginTop: 12 }}>
            <h5 style={{ fontFamily: fontStack, fontSize: 14, color: theme.cream, marginBottom: 8 }}>Mailto Setup</h5>
            <p style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted, lineHeight: 1.6, marginBottom: 12 }}>
              This opens the visitor's default email app with a pre-filled message. Simple but relies on the visitor having an email app configured.
            </p>
            <label style={s.label}>Church Email Address</label>
            <input style={s.input} value={draft.mailtoAddress} onChange={e => setDraft({ ...draft, mailtoAddress: e.target.value })} placeholder="info@npbcdryprong.com" />
          </div>
        )}
      </div>

      {/* Form Fields */}
      <h4 style={{ fontFamily: fontStack, fontSize: 20, fontWeight: 500, color: theme.cream, marginTop: 32, marginBottom: 16 }}>Form Fields ({fields.length})</h4>
      <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginBottom: 20 }}>
        Add, remove, reorder, and configure form fields. Toggle fields on/off without deleting. "Half Width" pairs two fields side by side.
      </p>

      {fields.map((f, idx) => (
        <div key={f.id} style={{ ...s.card, opacity: f.enabled ? 1 : 0.4, marginBottom: 8 }}>
          {editingField === f.id ? (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
                <div><label style={s.label}>Label</label><input style={s.input} value={f.label} onChange={e => updateField(f.id, { label: e.target.value })} /></div>
                <div><label style={s.label}>Type</label>
                  <select style={s.input} value={f.type} onChange={e => updateField(f.id, { type: e.target.value })}>
                    {fieldTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 12 }}><label style={s.label}>Placeholder</label><input style={s.input} value={f.placeholder || ""} onChange={e => updateField(f.id, { placeholder: e.target.value })} /></div>
              {f.type === "select" && (
                <div style={{ marginTop: 12 }}>
                  <label style={s.label}>Dropdown Options (comma-separated)</label>
                  <input style={s.input} value={f.options || ""} onChange={e => updateField(f.id, { options: e.target.value })} placeholder="Option A, Option B, Option C" />
                </div>
              )}
              <div style={{ display: "flex", gap: 20, marginTop: 12, flexWrap: "wrap" }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, cursor: "pointer" }}>
                  <input type="checkbox" checked={f.required} onChange={e => updateField(f.id, { required: e.target.checked })} /> Required
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, cursor: "pointer" }}>
                  <input type="checkbox" checked={f.halfWidth} onChange={e => updateField(f.id, { halfWidth: e.target.checked })} /> Half Width
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, cursor: "pointer" }}>
                  <input type="checkbox" checked={f.enabled} onChange={e => updateField(f.id, { enabled: e.target.checked })} /> Enabled
                </label>
              </div>
              <button style={{ ...s.btnPrimary, marginTop: 12 }} onClick={() => setEditingField(null)}>Done</button>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {/* Reorder arrows */}
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <button onClick={() => moveField(idx, -1)} style={{ background: "none", border: "none", color: idx === 0 ? theme.border : theme.textMuted, cursor: idx === 0 ? "default" : "pointer", fontSize: 14, padding: 0, lineHeight: 1 }}>▲</button>
                  <button onClick={() => moveField(idx, 1)} style={{ background: "none", border: "none", color: idx === fields.length - 1 ? theme.border : theme.textMuted, cursor: idx === fields.length - 1 ? "default" : "pointer", fontSize: 14, padding: 0, lineHeight: 1 }}>▼</button>
                </div>
                <div>
                  <span style={{ fontFamily: fontStack, fontSize: 16, fontWeight: 500, color: theme.cream }}>{f.label}</span>
                  <span style={{ fontFamily: bodyFont, fontSize: 11, color: theme.textMuted, marginLeft: 10 }}>
                    {f.type}{f.required ? " • required" : ""}{f.halfWidth ? " • ½" : ""}{!f.enabled ? " • disabled" : ""}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button style={s.btnOutline} onClick={() => updateField(f.id, { enabled: !f.enabled })}>{f.enabled ? "Disable" : "Enable"}</button>
                <button style={s.btnOutline} onClick={() => setEditingField(f.id)}>Edit</button>
                <button style={s.btnDanger} onClick={() => removeField(f.id)}>✕</button>
              </div>
            </div>
          )}
        </div>
      ))}

      <button style={{ ...s.btnPrimary, marginTop: 16 }} onClick={addField}>+ Add Field</button>
    </div>
  );
}

// ── Media Manager ──
function MediaTab() {
  const { data, setField, updateItem, addItem, removeItem } = useChurchData();
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);

  const emptyPhoto = { title: "", image: null, caption: "", enabled: true };
  const [newPhoto, setNewPhoto] = useState(emptyPhoto);

  const emptyDoc = { title: "", description: "", fileUrl: "", fileName: "", fileType: "pdf", staffOnly: false, enabled: true };
  const [newDoc, setNewDoc] = useState(emptyDoc);

  return (
    <div>
      {/* Section toggle */}
      <div style={{ ...s.card, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, borderColor: data.mediaEnabled ? "rgba(39,174,96,0.3)" : theme.border }}>
        <div>
          <h3 style={{ fontFamily: fontStack, fontSize: 20, fontWeight: 500, color: theme.cream }}>Media on Resources Page</h3>
          <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginTop: 4 }}>
            {data.mediaEnabled ? "Photos and documents are shown below YouTube content" : "Media sections are hidden from the Resources page"}
          </p>
        </div>
        <button
          style={{ ...s.btnPrimary, background: data.mediaEnabled ? "#27ae60" : "rgba(255,255,255,0.1)", minWidth: 100 }}
          onClick={() => setField("mediaEnabled", !data.mediaEnabled)}
        >
          {data.mediaEnabled ? "✓ Enabled" : "Disabled"}
        </button>
      </div>

      {/* ── Photos ── */}
      <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 8 }}>
        Photo Gallery ({(data.photos || []).length})
      </h3>
      <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginBottom: 12 }}>
        Add church photos by pasting direct image URLs. The image must be a direct link (ending in .jpg, .png, etc.) for it to display on the site.
      </p>
      <div style={{ padding: 16, background: "rgba(255,255,255,0.02)", border: `1px solid ${theme.border}`, marginBottom: 20, lineHeight: 1.8 }}>
        <h5 style={{ fontFamily: fontStack, fontSize: 14, color: theme.gold, marginBottom: 8 }}>Free Image Hosting Options</h5>
        <p style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted }}>
          <strong style={{ color: theme.cream }}>imgbb.com</strong> (easiest — no account needed)<br />
          → Go to imgbb.com → Click "Start Uploading" → Upload photo → Copy the "Direct link" URL<br /><br />
          <strong style={{ color: theme.cream }}>postimages.org</strong> (no account needed)<br />
          → Go to postimages.org → Upload photo → Copy the "Direct link" URL<br /><br />
          <strong style={{ color: theme.cream }}>imgur.com</strong> (account recommended)<br />
          → Upload photo → Right-click the image → "Copy image address" → Should look like <code style={{ background: "rgba(255,255,255,0.05)", padding: "1px 4px" }}>https://i.imgur.com/xxxxx.jpg</code>
        </p>
        <p style={{ fontFamily: bodyFont, fontSize: 11, color: theme.gold, marginTop: 8 }}>
          ⚠️ Google Drive and Google Photos links will NOT work — they don't provide direct image URLs.
        </p>
      </div>

      {/* Existing photos */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        {(data.photos || []).map((photo) => (
          <div key={photo.id} style={{ ...s.card, padding: 0, overflow: "hidden", opacity: photo.enabled ? 1 : 0.4, position: "relative" }}>
            {editingPhoto === photo.id ? (
              <div style={{ padding: 12 }}>
                <div><label style={s.label}>Image URL</label><input style={s.input} defaultValue={photo.image} id={`pu-${photo.id}`} placeholder="https://..." /></div>
                <div style={{ marginTop: 8 }}><label style={s.label}>Caption</label><input style={s.input} defaultValue={photo.caption} id={`pc-${photo.id}`} /></div>
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <button style={{ ...s.btnPrimary, padding: "6px 12px", fontSize: 10 }} onClick={() => {
                    updateItem("photos", photo.id, {
                      image: document.getElementById(`pu-${photo.id}`).value,
                      caption: document.getElementById(`pc-${photo.id}`).value,
                    });
                    setEditingPhoto(null);
                  }}>Save</button>
                  <button style={{ ...s.btnOutline, padding: "6px 12px", fontSize: 10 }} onClick={() => setEditingPhoto(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                {photo.image && <div style={{ height: 120, background: `url(${photo.image}) center/cover`, backgroundColor: theme.bgCard }} />}
                <div style={{ padding: 10 }}>
                  <p style={{ fontFamily: bodyFont, fontSize: 11, color: theme.cream, marginBottom: 6 }}>{photo.caption || photo.title || "Untitled"}</p>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                    <button style={{ ...s.btnOutline, padding: "4px 8px", fontSize: 9 }} onClick={() => updateItem("photos", photo.id, { enabled: !photo.enabled })}>
                      {photo.enabled ? "Hide" : "Show"}
                    </button>
                    <button style={{ ...s.btnOutline, padding: "4px 8px", fontSize: 9 }} onClick={() => setEditingPhoto(photo.id)}>Edit</button>
                    <button style={{ ...s.btnDanger, padding: "4px 8px", fontSize: 9 }} onClick={() => { if (window.confirm("Remove this photo?")) removeItem("photos", photo.id); }}>✕</button>
                  </div>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Add photo */}
      <div style={{ ...s.card, borderColor: theme.borderHover }}>
        <h4 style={{ fontFamily: fontStack, fontSize: 14, fontWeight: 500, color: theme.gold, marginBottom: 12 }}>+ Add Photo</h4>
        <div style={{ marginBottom: 12 }}>
          <label style={s.label}>Image URL</label>
          <input style={s.input} value={newPhoto.image || ""} onChange={e => setNewPhoto({ ...newPhoto, image: e.target.value })} placeholder="https://drive.google.com/uc?id=..." />
        </div>
        {newPhoto.image && <div style={{ width: 120, height: 80, background: `url(${newPhoto.image}) center/cover`, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}`, marginBottom: 12 }} />}
        <div><label style={s.label}>Caption</label><input style={s.input} value={newPhoto.caption} onChange={e => setNewPhoto({ ...newPhoto, caption: e.target.value })} placeholder="Optional caption" /></div>
        <button style={{ ...s.btnPrimary, marginTop: 12 }} onClick={() => {
          if (!newPhoto.image) return alert("Please paste an image URL");
          addItem("photos", { ...newPhoto, title: newPhoto.caption || `Photo ${(data.photos || []).length + 1}` });
          setNewPhoto(emptyPhoto);
        }}>Add Photo</button>
      </div>

      {/* ── Documents ── */}
      <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid ${theme.border}` }}>
        <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 8 }}>
          Documents ({(data.documents || []).length})
        </h3>
        <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginBottom: 20 }}>
          Add links to documents (PDFs, Word docs, etc.) hosted online. Use Google Drive, Dropbox, or any file hosting service and paste the public link. Toggle "Staff Only" to hide from public visitors.
        </p>

        {(data.documents || []).map((doc) => (
          <div key={doc.id} style={{ ...s.card, opacity: doc.enabled ? 1 : 0.4 }}>
            {editingDoc === doc.id ? (
              <DocumentForm doc={doc}
                onSave={(updates) => { updateItem("documents", doc.id, updates); setEditingDoc(null); }}
                onCancel={() => setEditingDoc(null)}
              />
            ) : (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                  <span style={{ fontSize: 24 }}>{doc.fileType === "pdf" ? "📄" : doc.fileType === "doc" || doc.fileType === "docx" ? "📝" : "📎"}</span>
                  <div>
                    <h4 style={{ fontFamily: fontStack, fontSize: 16, fontWeight: 500, color: theme.cream }}>{doc.title}</h4>
                    <div style={{ fontFamily: bodyFont, fontSize: 11, color: theme.textMuted }}>
                      .{doc.fileType || "file"}
                      {doc.staffOnly && <span style={{ color: "#e74c3c", marginLeft: 8 }}>🔒 Staff Only</span>}
                      {!doc.enabled && <span style={{ marginLeft: 8 }}>• Hidden</span>}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button style={s.btnOutline} onClick={() => updateItem("documents", doc.id, { enabled: !doc.enabled })}>
                    {doc.enabled ? "Hide" : "Show"}
                  </button>
                  <button style={s.btnOutline} onClick={() => setEditingDoc(doc.id)}>Edit</button>
                  <button style={s.btnDanger} onClick={() => { if (window.confirm(`Remove "${doc.title}"?`)) removeItem("documents", doc.id); }}>Remove</button>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Add document */}
        <div style={{ ...s.card, borderColor: theme.borderHover, marginTop: 16 }}>
          <h4 style={{ fontFamily: fontStack, fontSize: 14, fontWeight: 500, color: theme.gold, marginBottom: 12 }}>+ Add Document</h4>
          <DocumentForm doc={newDoc} isNew
            onSave={(d) => { addItem("documents", d); setNewDoc(emptyDoc); }}
            onCancel={() => setNewDoc(emptyDoc)}
          />
        </div>
      </div>
    </div>
  );
}

// ── Document Form ──
function DocumentForm({ doc, onSave, onCancel, isNew }) {
  const [draft, setDraft] = useState({ ...doc });
  const fileTypes = ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt", "other"];

  return (
    <div>
      <div><label style={s.label}>Document Title</label><input style={s.input} value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} placeholder="e.g. March 2026 Bulletin" /></div>
      <div style={{ marginTop: 12 }}><label style={s.label}>Description</label><input style={s.input} value={draft.description || ""} onChange={e => setDraft({ ...draft, description: e.target.value })} placeholder="Brief description (optional)" /></div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginTop: 12 }}>
        <div>
          <label style={s.label}>File URL (Google Drive, Dropbox, etc.)</label>
          <input style={s.input} value={draft.fileUrl || ""} onChange={e => setDraft({ ...draft, fileUrl: e.target.value })} placeholder="https://drive.google.com/..." />
        </div>
        <div>
          <label style={s.label}>File Type</label>
          <select style={s.input} value={draft.fileType || "pdf"} onChange={e => setDraft({ ...draft, fileType: e.target.value })}>
            {fileTypes.map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: "flex", gap: 20, marginTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={draft.staffOnly || false} onChange={e => setDraft({ ...draft, staffOnly: e.target.checked })} id={`so-${doc.id || "new"}`} />
          <label htmlFor={`so-${doc.id || "new"}`} style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted }}>🔒 Staff Only (hidden from public)</label>
        </div>
        {!isNew && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" checked={draft.enabled} onChange={e => setDraft({ ...draft, enabled: e.target.checked })} id={`en-${doc.id}`} />
            <label htmlFor={`en-${doc.id}`} style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted }}>Visible</label>
          </div>
        )}
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button style={s.btnPrimary} onClick={() => {
          if (!draft.title.trim()) return alert("Title is required");
          if (!draft.fileUrl.trim()) return alert("File URL is required");
          onSave(draft);
        }}>{isNew ? "Add Document" : "Save"}</button>
        {!isNew && <button style={s.btnOutline} onClick={onCancel}>Cancel</button>}
      </div>
    </div>
  );
}

// ── Giving Manager ──
function GivingTab() {
  const { data, setField, updateItem, addItem, removeItem } = useChurchData();
  const [editing, setEditing] = useState(null);
  const [editingFund, setEditingFund] = useState(null);

  const paymentTypes = [
    { value: "text-to-give", label: "Text-to-Give" },
    { value: "online", label: "Online Giving" },
    { value: "paypal", label: "PayPal" },
    { value: "venmo", label: "Venmo" },
    { value: "cashapp", label: "Cash App" },
    { value: "qr-code", label: "QR Code" },
    { value: "embed", label: "Embed Code (Tithe.ly, etc)" },
    { value: "link", label: "External Link" },
    { value: "other", label: "Other" },
  ];

  const emptyMethod = { type: "link", label: "", instructions: "", link: "", linkLabel: "Give Now", embedCode: "", qrImage: null, icon: "💝", enabled: true };
  const [newMethod, setNewMethod] = useState(emptyMethod);

  const emptyCampaign = { title: "", description: "", goalAmount: 0, currentAmount: 0, deadline: "", donateLink: "", image: null, active: true };
  const [newCampaign, setNewCampaign] = useState(emptyCampaign);

  return (
    <div>
      {/* Page toggle */}
      <div style={{ ...s.card, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, borderColor: data.givingEnabled ? "rgba(39,174,96,0.3)" : theme.border }}>
        <div>
          <h3 style={{ fontFamily: fontStack, fontSize: 20, fontWeight: 500, color: theme.cream }}>Giving Page</h3>
          <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginTop: 4 }}>
            {data.givingEnabled ? "Visible in navigation — visitors can see this page" : "Hidden from navigation — only accessible via direct link"}
          </p>
        </div>
        <button
          style={{ ...s.btnPrimary, background: data.givingEnabled ? "#27ae60" : "rgba(255,255,255,0.1)", minWidth: 100 }}
          onClick={() => setField("givingEnabled", !data.givingEnabled)}
        >
          {data.givingEnabled ? "✓ Enabled" : "Disabled"}
        </button>
      </div>

      {/* ── Payment Methods ── */}
      <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 8 }}>
        Payment Methods ({(data.paymentMethods || []).length})
      </h3>
      <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginBottom: 20 }}>
        Configure how members can give. You can add links, embed codes from services like Tithe.ly or PayPal, QR codes, or simple text instructions.
      </p>

      {(data.paymentMethods || []).map((method) => (
        <div key={method.id} style={{ ...s.card, borderColor: method.enabled ? theme.border : "rgba(255,255,255,0.03)", opacity: method.enabled ? 1 : 0.5 }}>
          {editing === method.id ? (
            <PaymentMethodForm method={method} paymentTypes={paymentTypes}
              onSave={(updates) => { updateItem("paymentMethods", method.id, updates); setEditing(null); }}
              onCancel={() => setEditing(null)}
            />
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1 }}>
                <span style={{ fontSize: 24 }}>{method.icon || "💝"}</span>
                <div>
                  <h4 style={{ fontFamily: fontStack, fontSize: 17, fontWeight: 500, color: theme.cream }}>{method.label}</h4>
                  <span style={{ fontFamily: bodyFont, fontSize: 11, color: theme.textMuted }}>{method.type} {method.link ? `• ${method.link.substring(0, 40)}...` : ""}</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button style={s.btnOutline} onClick={() => updateItem("paymentMethods", method.id, { enabled: !method.enabled })}>
                  {method.enabled ? "Disable" : "Enable"}
                </button>
                <button style={s.btnOutline} onClick={() => setEditing(method.id)}>Edit</button>
                <button style={s.btnDanger} onClick={() => { if (window.confirm(`Remove "${method.label}"?`)) removeItem("paymentMethods", method.id); }}>Remove</button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add payment method */}
      <div style={{ ...s.card, borderColor: theme.borderHover, marginTop: 16 }}>
        <h4 style={{ fontFamily: fontStack, fontSize: 16, fontWeight: 500, color: theme.gold, marginBottom: 16 }}>+ Add Payment Method</h4>
        <PaymentMethodForm method={newMethod} paymentTypes={paymentTypes} isNew
          onSave={(m) => { addItem("paymentMethods", m); setNewMethod(emptyMethod); }}
          onCancel={() => setNewMethod(emptyMethod)}
        />
      </div>

      {/* ── Fundraiser Campaigns ── */}
      <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid ${theme.border}` }}>
        <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 8 }}>
          Fundraiser Campaigns ({(data.fundraisers || []).length})
        </h3>
        <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginBottom: 20 }}>
          Create campaigns with goals, progress tracking, and deadlines. Only active campaigns are shown to visitors.
        </p>

        {(data.fundraisers || []).map((fund) => (
          <div key={fund.id} style={{ ...s.card, opacity: fund.active ? 1 : 0.5 }}>
            {editingFund === fund.id ? (
              <FundraiserForm campaign={fund}
                onSave={(updates) => { updateItem("fundraisers", fund.id, updates); setEditingFund(null); }}
                onCancel={() => setEditingFund(null)}
              />
            ) : (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <h4 style={{ fontFamily: fontStack, fontSize: 17, fontWeight: 500, color: theme.cream }}>{fund.title}</h4>
                    {!fund.active && <span style={{ fontFamily: bodyFont, fontSize: 9, color: theme.textMuted, background: "rgba(255,255,255,0.05)", padding: "2px 6px" }}>INACTIVE</span>}
                  </div>
                  <div style={{ fontFamily: bodyFont, fontSize: 12, color: theme.textMuted, marginTop: 4 }}>
                    {fund.goalAmount > 0 && <span>${(fund.currentAmount || 0).toLocaleString()} / ${fund.goalAmount.toLocaleString()}</span>}
                    {fund.deadline && <span> • Due: {fund.deadline}</span>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button style={s.btnOutline} onClick={() => updateItem("fundraisers", fund.id, { active: !fund.active })}>
                    {fund.active ? "Deactivate" : "Activate"}
                  </button>
                  <button style={s.btnOutline} onClick={() => setEditingFund(fund.id)}>Edit</button>
                  <button style={s.btnDanger} onClick={() => { if (window.confirm(`Remove "${fund.title}"?`)) removeItem("fundraisers", fund.id); }}>Remove</button>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Add fundraiser */}
        <div style={{ ...s.card, borderColor: theme.borderHover, marginTop: 16 }}>
          <h4 style={{ fontFamily: fontStack, fontSize: 16, fontWeight: 500, color: theme.gold, marginBottom: 16 }}>+ Add Fundraiser Campaign</h4>
          <FundraiserForm campaign={newCampaign} isNew
            onSave={(c) => { addItem("fundraisers", c); setNewCampaign(emptyCampaign); }}
            onCancel={() => setNewCampaign(emptyCampaign)}
          />
        </div>
      </div>
    </div>
  );
}

// ── Payment Method Form ──
function PaymentMethodForm({ method, paymentTypes, onSave, onCancel, isNew }) {
  const [draft, setDraft] = useState({ ...method });
  const fileRef = useRef();

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <label style={s.label}>Label</label>
          <input style={s.input} value={draft.label} onChange={e => setDraft({ ...draft, label: e.target.value })} placeholder="e.g. Online Giving, PayPal, Cash App" />
        </div>
        <div>
          <label style={s.label}>Type</label>
          <select style={{ ...s.input }} value={draft.type} onChange={e => setDraft({ ...draft, type: e.target.value })}>
            {paymentTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <label style={s.label}>Instructions / Description</label>
        <textarea style={s.textarea} value={draft.instructions || ""} onChange={e => setDraft({ ...draft, instructions: e.target.value })} placeholder="How to use this giving method..." />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
        <div>
          <label style={s.label}>Button Link (URL)</label>
          <input style={s.input} value={draft.link || ""} onChange={e => setDraft({ ...draft, link: e.target.value })} placeholder="https://give.tithe.ly/..." />
        </div>
        <div>
          <label style={s.label}>Button Label</label>
          <input style={s.input} value={draft.linkLabel || ""} onChange={e => setDraft({ ...draft, linkLabel: e.target.value })} placeholder="Give Now" />
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <label style={s.label}>Embed Code (paste HTML from Tithe.ly, PayPal, etc)</label>
        <textarea style={{ ...s.textarea, fontFamily: "monospace", fontSize: 12 }} value={draft.embedCode || ""} onChange={e => setDraft({ ...draft, embedCode: e.target.value })} placeholder='<script src="https://tithe.ly/widget/give?..." />' />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
        <div>
          <label style={s.label}>Icon (emoji)</label>
          <input style={s.input} value={draft.icon || ""} onChange={e => setDraft({ ...draft, icon: e.target.value })} placeholder="💝" />
        </div>
        <div>
          <label style={s.label}>QR Code Image</label>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {draft.qrImage && <img src={draft.qrImage} alt="QR" style={{ width: 40, height: 40, border: `1px solid ${theme.border}` }} />}
            <button style={s.btnOutline} onClick={() => fileRef.current?.click()}>
              {draft.qrImage ? "Change" : "Upload QR"}
            </button>
            {draft.qrImage && <button style={{ ...s.btnOutline, color: "#e74c3c", borderColor: "#c0392b" }} onClick={() => setDraft({ ...draft, qrImage: null })}>Remove</button>}
            <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={async e => {
              const file = e.target.files[0];
              if (!file) return;
              try {
                const base64 = await compressImage(file, 200, 200, 0.7);
                setDraft({ ...draft, qrImage: base64 });
              } catch { alert("Failed to process image"); }
            }} />
          </div>
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button style={s.btnPrimary} onClick={() => {
          if (!draft.label.trim()) return alert("Label is required");
          onSave(draft);
        }}>{isNew ? "Add Payment Method" : "Save"}</button>
        {!isNew && <button style={s.btnOutline} onClick={onCancel}>Cancel</button>}
      </div>
    </div>
  );
}

// ── Fundraiser Form ──
function FundraiserForm({ campaign, onSave, onCancel, isNew }) {
  const [draft, setDraft] = useState({ ...campaign });

  return (
    <div>
      <div><label style={s.label}>Campaign Title</label><input style={s.input} value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} placeholder="e.g. Building Fund, Mission Trip" /></div>
      <div style={{ marginTop: 12 }}><label style={s.label}>Description</label><textarea style={s.textarea} value={draft.description} onChange={e => setDraft({ ...draft, description: e.target.value })} placeholder="What this fundraiser supports..." /></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 12 }}>
        <div>
          <label style={s.label}>Goal Amount ($)</label>
          <input style={s.input} type="number" value={draft.goalAmount || ""} onChange={e => setDraft({ ...draft, goalAmount: parseFloat(e.target.value) || 0 })} placeholder="5000" />
        </div>
        <div>
          <label style={s.label}>Current Amount ($)</label>
          <input style={s.input} type="number" value={draft.currentAmount || ""} onChange={e => setDraft({ ...draft, currentAmount: parseFloat(e.target.value) || 0 })} placeholder="0" />
        </div>
        <div>
          <label style={s.label}>Deadline</label>
          <input style={s.input} type="date" value={draft.deadline || ""} onChange={e => setDraft({ ...draft, deadline: e.target.value })} />
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <label style={s.label}>Donate Link (URL)</label>
        <input style={s.input} value={draft.donateLink || ""} onChange={e => setDraft({ ...draft, donateLink: e.target.value })} placeholder="https://..." />
      </div>
      <div style={{ marginTop: 12 }}>
        <label style={s.label}>Campaign Image URL (optional — use imgbb.com or postimages.org for free hosting)</label>
        <input style={s.input} value={draft.image || ""} onChange={e => setDraft({ ...draft, image: e.target.value })} placeholder="https://drive.google.com/uc?id=..." />
        {draft.image && <div style={{ width: 120, height: 80, marginTop: 8, background: `url(${draft.image}) center/cover`, backgroundColor: theme.bgCard, border: `1px solid ${theme.border}` }} />}
      </div>
      {!isNew && (
        <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={draft.active} onChange={e => setDraft({ ...draft, active: e.target.checked })} id="fund-active" />
          <label htmlFor="fund-active" style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted }}>Active (visible to visitors)</label>
        </div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button style={s.btnPrimary} onClick={() => {
          if (!draft.title.trim()) return alert("Title is required");
          onSave(draft);
        }}>{isNew ? "Add Fundraiser" : "Save"}</button>
        {!isNew && <button style={s.btnOutline} onClick={onCancel}>Cancel</button>}
      </div>
    </div>
  );
}

// ── Services Manager ──
function ServicesTab() {
  const { data, updateItem, addItem, removeItem } = useChurchData();
  const [editing, setEditing] = useState(null);
  const [editingHero, setEditingHero] = useState(null);
  const emptyService = { name: "", time: "" };
  const [newService, setNewService] = useState(emptyService);
  const [newHero, setNewHero] = useState(emptyService);

  return (
    <div>
      {/* ── All Service Times ── */}
      <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 8 }}>
        All Service Times ({data.services.length})
      </h3>
      <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginBottom: 20 }}>
        These are displayed on the homepage in the full "Service Times" section.
      </p>

      {data.services.map((svc) => (
        <div key={svc.id} style={s.card}>
          {editing === svc.id ? (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div><label style={s.label}>Service Name</label><input style={s.input} defaultValue={svc.name} id={`svc-name-${svc.id}`} /></div>
                <div><label style={s.label}>Time</label><input style={s.input} defaultValue={svc.time} id={`svc-time-${svc.id}`} /></div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button style={s.btnPrimary} onClick={() => {
                  updateItem("services", svc.id, {
                    name: document.getElementById(`svc-name-${svc.id}`).value,
                    time: document.getElementById(`svc-time-${svc.id}`).value,
                  });
                  setEditing(null);
                }}>Save</button>
                <button style={s.btnOutline} onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <span style={{ fontFamily: bodyFont, fontSize: 15, color: theme.cream }}>{svc.name}</span>
                <span style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginLeft: 12 }}>—</span>
                <span style={{ fontFamily: fontStack, fontSize: 17, fontWeight: 500, color: theme.gold, marginLeft: 12 }}>{svc.time}</span>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button style={s.btnOutline} onClick={() => setEditing(svc.id)}>Edit</button>
                <button style={s.btnDanger} onClick={() => { if (window.confirm(`Remove "${svc.name}"?`)) removeItem("services", svc.id); }}>Remove</button>
              </div>
            </div>
          )}
        </div>
      ))}

      <div style={{ ...s.card, borderColor: theme.borderHover, marginTop: 16 }}>
        <h4 style={{ fontFamily: fontStack, fontSize: 14, fontWeight: 500, color: theme.gold, marginBottom: 12 }}>+ Add Service</h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label style={s.label}>Service Name</label><input style={s.input} value={newService.name} onChange={e => setNewService({ ...newService, name: e.target.value })} placeholder="e.g. Friday Night Prayer" /></div>
          <div><label style={s.label}>Time</label><input style={s.input} value={newService.time} onChange={e => setNewService({ ...newService, time: e.target.value })} placeholder="e.g. 7:00 PM" /></div>
        </div>
        <button style={{ ...s.btnPrimary, marginTop: 12 }} onClick={() => {
          if (!newService.name.trim()) return alert("Service name is required");
          addItem("services", { ...newService });
          setNewService(emptyService);
        }}>Add Service</button>
      </div>

      {/* ── Hero Services (shown at top of homepage) ── */}
      <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid ${theme.border}` }}>
        <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 8 }}>
          Hero Services ({data.heroServices.length})
        </h3>
        <p style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginBottom: 20 }}>
          These are the featured services shown prominently in the hero area at the top of the homepage. Typically 2-3 services.
        </p>

        {data.heroServices.map((svc) => (
          <div key={svc.id} style={s.card}>
            {editingHero === svc.id ? (
              <div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div><label style={s.label}>Service Name</label><input style={s.input} defaultValue={svc.name} id={`hero-name-${svc.id}`} /></div>
                  <div><label style={s.label}>Time</label><input style={s.input} defaultValue={svc.time} id={`hero-time-${svc.id}`} /></div>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button style={s.btnPrimary} onClick={() => {
                    updateItem("heroServices", svc.id, {
                      name: document.getElementById(`hero-name-${svc.id}`).value,
                      time: document.getElementById(`hero-time-${svc.id}`).value,
                    });
                    setEditingHero(null);
                  }}>Save</button>
                  <button style={s.btnOutline} onClick={() => setEditingHero(null)}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ fontFamily: bodyFont, fontSize: 15, color: theme.cream }}>{svc.name}</span>
                  <span style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, marginLeft: 12 }}>—</span>
                  <span style={{ fontFamily: fontStack, fontSize: 17, fontWeight: 500, color: theme.gold, marginLeft: 12 }}>{svc.time}</span>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={s.btnOutline} onClick={() => setEditingHero(svc.id)}>Edit</button>
                  <button style={s.btnDanger} onClick={() => { if (window.confirm(`Remove "${svc.name}" from hero?`)) removeItem("heroServices", svc.id); }}>Remove</button>
                </div>
              </div>
            )}
          </div>
        ))}

        <div style={{ ...s.card, borderColor: theme.borderHover, marginTop: 16 }}>
          <h4 style={{ fontFamily: fontStack, fontSize: 14, fontWeight: 500, color: theme.gold, marginBottom: 12 }}>+ Add Hero Service</h4>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label style={s.label}>Service Name</label><input style={s.input} value={newHero.name} onChange={e => setNewHero({ ...newHero, name: e.target.value })} placeholder="e.g. Sunday Worship" /></div>
            <div><label style={s.label}>Time</label><input style={s.input} value={newHero.time} onChange={e => setNewHero({ ...newHero, time: e.target.value })} placeholder="e.g. 10:30 AM" /></div>
          </div>
          <button style={{ ...s.btnPrimary, marginTop: 12 }} onClick={() => {
            if (!newHero.name.trim()) return alert("Service name is required");
            addItem("heroServices", { ...newHero });
            setNewHero(emptyService);
          }}>Add Hero Service</button>
        </div>
      </div>
    </div>
  );
}

// ── Photo Upload Component ──
// ── Image Resize Utility ──
// Strategy: resize to small dimensions, keep quality high
function compressImage(file, maxWidth = 150, maxHeight = 200, quality = 0.75) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let w = img.width, h = img.height;
        if (w > maxWidth || h > maxHeight) {
          const ratio = Math.min(maxWidth / w, maxHeight / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, w, h);
        const base64 = canvas.toDataURL("image/jpeg", quality);
        resolve(base64);
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = e.target.result;
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

function PhotoUpload({ onUpload, currentPhoto, maxWidth, maxHeight, quality }) {
  const fileRef = useRef();
  const [compressing, setCompressing] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCompressing(true);
    try {
      const base64 = await compressImage(file, maxWidth || 150, maxHeight || 200, quality || 0.75);
      const sizeKB = Math.round(base64.length / 1024);
      console.log(`Photo: ${maxWidth || 150}x${maxHeight || 200} q${quality || 0.75} → ${sizeKB}KB`);
      onUpload(base64);
    } catch (err) {
      alert("Failed to process image. Please try a different file.");
    }
    setCompressing(false);
  };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {currentPhoto && (
        <div style={{ width: 50, height: 50, borderRadius: "50%", background: `url(${currentPhoto}) center/cover`, border: `2px solid ${theme.border}` }} />
      )}
      <button style={s.btnOutline} onClick={() => fileRef.current?.click()} disabled={compressing}>
        {compressing ? "Resizing..." : currentPhoto ? "Change Photo" : "Upload Photo"}
      </button>
      {currentPhoto && <button style={{ ...s.btnOutline, color: "#e74c3c", borderColor: "#c0392b" }} onClick={() => onUpload(null)}>Remove</button>}
      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}


// ── Generic Edit Form ──
const bulletinFields = [
  { key: "date", label: "Date", type: "text" },
  { key: "category", label: "Category", type: "text" },
  { key: "title", label: "Title", type: "text" },
  { key: "desc", label: "Description", type: "textarea" },
  { key: "featured", label: "Featured", type: "checkbox" },
];

function EditForm({ item, fields, onSave, onCancel }) {
  const [draft, setDraft] = useState({ ...item });
  return (
    <div>
      {fields.map(f => (
        <div key={f.key} style={{ marginBottom: 12 }}>
          <label style={s.label}>{f.label}</label>
          {f.type === "textarea" ? (
            <textarea style={s.textarea} value={draft[f.key] || ""} onChange={e => setDraft({ ...draft, [f.key]: e.target.value })} />
          ) : f.type === "checkbox" ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="checkbox" checked={!!draft[f.key]} onChange={e => setDraft({ ...draft, [f.key]: e.target.checked })} />
              <span style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted }}>Yes</span>
            </div>
          ) : (
            <input style={s.input} value={draft[f.key] || ""} onChange={e => setDraft({ ...draft, [f.key]: e.target.value })} />
          )}
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button style={s.btnPrimary} onClick={() => onSave(draft)}>Save</button>
        <button style={s.btnOutline} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Staff Edit Form (with photo) ──
function StaffEditForm({ person, onSave, onCancel }) {
  const [draft, setDraft] = useState({ ...person });
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div><label style={s.label}>Name</label><input style={s.input} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></div>
        <div><label style={s.label}>Role</label><input style={s.input} value={draft.role} onChange={e => setDraft({ ...draft, role: e.target.value })} /></div>
      </div>
      <div style={{ marginTop: 12 }}><label style={s.label}>Description</label><textarea style={s.textarea} value={draft.desc} onChange={e => setDraft({ ...draft, desc: e.target.value })} /></div>
      <div style={{ marginTop: 12 }}><label style={s.label}>Photo</label><PhotoUpload onUpload={(base64) => setDraft({ ...draft, photo: base64 })} currentPhoto={draft.photo} /></div>
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button style={s.btnPrimary} onClick={() => onSave(draft)}>Save</button>
        <button style={s.btnOutline} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Ministry Edit Form ──
function MinistryEditForm({ ministry, iconOptions, onSave, onCancel }) {
  const [draft, setDraft] = useState({ ...ministry });
  return (
    <div>
      <div><label style={s.label}>Name</label><input style={s.input} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} /></div>
      <div style={{ marginTop: 12 }}><label style={s.label}>Description</label><textarea style={s.textarea} value={draft.desc} onChange={e => setDraft({ ...draft, desc: e.target.value })} /></div>
      <div style={{ marginTop: 12 }}>
        <label style={s.label}>Icon</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {iconOptions.map(ico => (
            <button key={ico} onClick={() => setDraft({ ...draft, icon: ico })}
              style={{
                width: 36, height: 36, fontSize: 18, cursor: "pointer",
                background: draft.icon === ico ? "rgba(200,164,86,0.2)" : "rgba(255,255,255,0.03)",
                border: `2px solid ${draft.icon === ico ? theme.gold : theme.border}`,
                borderRadius: 4,
              }}>{ico}</button>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
        <button style={s.btnPrimary} onClick={() => onSave(draft)}>Save</button>
        <button style={s.btnOutline} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Main Admin Portal ──
export default function AdminPage() {
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const { data, saveData, exportBackup, resetToDefaults, saveStatus, isRemoteConfigured } = useChurchData();
  const dataSizeKB = Math.round(new Blob([JSON.stringify(data)]).size / 1024);

  if (!authenticated) {
    return <LoginScreen onLogin={() => setAuthenticated(true)} />;
  }

  const tabs = [
    { key: "info", label: "Church Info", icon: "⛪" },
    { key: "bulletin", label: "Bulletin", icon: "📰" },
    { key: "staff", label: "Staff", icon: "👥" },
    { key: "ministries", label: "Ministries", icon: "🙏" },
    { key: "services", label: "Services", icon: "🕐" },
    { key: "giving", label: "Giving", icon: "💝" },
    { key: "media", label: "Media", icon: "📸" },
    { key: "form", label: "Contact Form", icon: "✉️" },
  ];

  const saveLabel = saveStatus === "saving" ? "Saving..." : saveStatus === "saved" ? "✓ Saved!" : saveStatus === "error" ? "✗ Error" : "Save Changes";
  const saveBtnStyle = {
    ...s.btnPrimary,
    background: saveStatus === "saved" ? "#27ae60" : saveStatus === "error" ? "#e74c3c" : theme.gold,
    opacity: saveStatus === "saving" ? 0.7 : 1,
    pointerEvents: saveStatus === "saving" ? "none" : "auto",
  };

  return (
    <div style={{ paddingTop: 100, minHeight: "100vh" }}>
      <section style={{ padding: "40px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>

          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 32, flexWrap: "wrap", gap: 16 }}>
            <div>
              <div style={{ fontFamily: bodyFont, fontSize: 11, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 4 }}>Admin Portal</div>
              <h1 style={{ fontFamily: fontStack, fontSize: 32, fontWeight: 400, color: theme.cream }}>Church Dashboard</h1>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={saveBtnStyle} onClick={saveData}>
                {saveLabel}
              </button>
              <button style={s.btnOutline} onClick={exportBackup} title="Download a backup copy">
                ⬇ Backup
              </button>
              <button style={s.btnOutline} onClick={() => { if (window.confirm("Reset all changes to original values?")) resetToDefaults(); }}>
                Reset
              </button>
              <button style={{ ...s.btnOutline, color: "#e74c3c", borderColor: "#c0392b" }} onClick={() => setAuthenticated(false)}>
                Logout
              </button>
            </div>
          </div>

          {/* Info banner */}
          {isRemoteConfigured ? (
            <div style={{
              padding: "14px 20px", marginBottom: 24,
              background: "rgba(39,174,96,0.06)", border: `1px solid rgba(39,174,96,0.2)`,
              fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, lineHeight: 1.6,
            }}>
              <strong style={{ color: "#2ecc71" }}>✓ Connected</strong> — Make your changes below, then click <strong style={{ color: theme.cream }}>"Save Changes"</strong> to publish instantly.
              <span style={{ float: "right", fontSize: 11, color: dataSizeKB > 90 ? "#e74c3c" : dataSizeKB > 70 ? "#f39c12" : "#2ecc71" }}>
                Data: {dataSizeKB}KB / 100KB {dataSizeKB > 90 ? "🔴 Almost full!" : dataSizeKB > 70 ? "🟡 Getting full" : ""}
              </span>
            </div>
          ) : (
            <div style={{
              padding: "14px 20px", marginBottom: 24,
              background: "rgba(200,164,86,0.06)", border: `1px solid ${theme.border}`,
              fontFamily: bodyFont, fontSize: 13, color: theme.textMuted, lineHeight: 1.6,
            }}>
              <strong style={{ color: theme.gold }}>Setup needed:</strong> Remote storage is not configured yet. Changes will be saved as a downloadable file.
              See <code style={{ color: theme.cream, background: "rgba(255,255,255,0.05)", padding: "1px 5px" }}>src/data/ChurchDataProvider.js</code> to connect JSONBin for instant saves.
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: "flex", gap: 4, marginBottom: 32, flexWrap: "wrap" }}>
            {tabs.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                style={{
                  padding: "12px 28px", display: "flex", alignItems: "center", gap: 8,
                  background: activeTab === t.key ? theme.gold : "transparent",
                  border: `1px solid ${activeTab === t.key ? theme.gold : theme.border}`,
                  color: activeTab === t.key ? theme.bg : theme.textMuted,
                  fontFamily: bodyFont, fontSize: 12, fontWeight: 600,
                  letterSpacing: "0.1em", textTransform: "uppercase",
                  cursor: "pointer", transition: "all 0.3s",
                }}
              >
                <span>{t.icon}</span> {t.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === "info" && <ChurchInfoTab />}
          {activeTab === "bulletin" && <BulletinTab />}
          {activeTab === "staff" && <StaffTab />}
          {activeTab === "ministries" && <MinistriesTab />}
          {activeTab === "services" && <ServicesTab />}
          {activeTab === "giving" && <GivingTab />}
          {activeTab === "media" && <MediaTab />}
          {activeTab === "form" && <FormTab />}
        </div>
      </section>
    </div>
  );
}
